// PWA — 电源面板状态 Hook（Req 6.1, 6.2, 6.3, 6.5 / 任务 9.3）
//
// 职责：
//  - 进入即 `GET /power/status` 显示 running / stopped / starting 等状态（Req 6.1）。
//  - stopped 时提供 `start()`：`POST /power/start` → 轮询 `GET /power/status` 至 running
//    → `GET /ready` 探测 ComfyUI 就绪（覆盖 ~40s 冷加载），期间显示进度 / 预计等待
//    （Req 6.2, 6.3, 6.5）。
//  - 复用 shared 的 `power`（mapEc2State / decideStart，任务 2.3/2.19）逻辑。
//
// 认证由上层传入的 `authedFetch`（见 auth/authedFetch.ts）承载，自动附带 JWT。
// 全部为出站请求，符合零入站架构。

import { useCallback, useEffect, useRef, useState } from "react";
import type { PowerState, PowerStatusResponse } from "@comfy/shared";

/** `authedFetch` 的最小签名（与 useAuth 暴露的一致）。 */
export type AuthedFetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface UsePowerOptions {
  /** 带认证的 fetch（自动附带 Bearer token）。 */
  authedFetch: AuthedFetch;
  /** 是否启用（通常等于「已认证」）。false 时不发起任何请求。 */
  enabled: boolean;
  /** 轮询间隔（ms），默认 4000。 */
  pollIntervalMs?: number;
}

export interface UsePowerResult {
  /** 当前电源状态（服务端经 mapEc2State 归一）。 */
  state: PowerState;
  /** ComfyUI 是否就绪（冷加载完成）。 */
  comfyReady: boolean;
  /** 目标实例 ID（诊断展示用）。 */
  instanceId: string | null;
  /** 初次状态查询进行中。 */
  loading: boolean;
  /** 「用户触发启动」的进行中流程（从 POST /power/start 到 ready）。 */
  startingFlow: boolean;
  /** 启动流程已耗时（秒），驱动进度 / 预计等待展示。 */
  elapsedSec: number;
  /** 最近一次错误信息。 */
  error: string | null;
  /** 手动刷新一次状态。 */
  refresh: () => void;
  /** 触发启动并轮询至就绪。 */
  start: () => void;
  /**
   * 触发关机（管理员密码保护）：POST /power/stop { passcode }。
   * 成功 resolve true 并置状态为 stopping；密码错误 resolve false 并设置 error。
   */
  stop: (passcode: string) => Promise<boolean>;
  /** 后端提示应修改关机密码（首发临时密码首次使用后为 true）。 */
  mustChangePasscode: boolean;
  /** 清除「应改密」提示（用户改完或稍后再说）。 */
  clearMustChangePasscode: () => void;
  /**
   * 修改关机密码：POST /power/passcode { currentPasscode, newPasscode }。
   * 成功 resolve { ok: true }；失败 resolve { ok: false, message }（当前密码错 / 新密码过短等）。
   */
  changePasscode: (
    currentPasscode: string,
    newPasscode: string,
  ) => Promise<{ ok: boolean; message?: string }>;
}

const DEFAULT_POLL_MS = 4000;

export function usePower(opts: UsePowerOptions): UsePowerResult {
  const { authedFetch, enabled, pollIntervalMs = DEFAULT_POLL_MS } = opts;

  const [state, setState] = useState<PowerState>("unavailable");
  const [comfyReady, setComfyReady] = useState(false);
  const [instanceId, setInstanceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [startingFlow, setStartingFlow] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [mustChangePasscode, setMustChangePasscode] = useState(false);

  // 挂载标记：卸载后不再 setState，避免竞态告警。
  const mountedRef = useRef(true);
  // 启动流程的取消令牌：递增即令旧流程失效。
  const flowRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      flowRef.current += 1; // 卸载即取消进行中的启动流程
    };
  }, []);

  // GET /power/status → PowerStatusResponse
  const fetchStatus = useCallback(async (): Promise<PowerStatusResponse | null> => {
    const res = await authedFetch("/power/status");
    if (!res.ok) {
      throw new Error(`获取电源状态失败（${res.status}）`);
    }
    return (await res.json()) as PowerStatusResponse;
  }, [authedFetch]);

  // 初次进入 / enabled 变化时查询一次状态（Req 6.1）。
  const refresh = useCallback(() => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    fetchStatus()
      .then((body) => {
        if (!mountedRef.current || !body) return;
        setState(body.state);
        setComfyReady(Boolean(body.comfyReady));
        setInstanceId(body.instanceId ?? null);
      })
      .catch((err: unknown) => {
        if (!mountedRef.current) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [enabled, fetchStatus]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

  // 独立于 start() 的就绪探测（Req 6.3, 6.5）：
  // 当实例已 running 但尚未就绪、且不处于用户触发的启动流程时，自动轮询 /ready
  // 直至 comfyReady。覆盖两种「非经 start() 进入 running」的情形：
  //   1) 进入应用时实例本就 running（/power/status 不含 comfyReady，需另探 /ready）；
  //   2) 实例在别处（控制台 / CLI）被启动。
  // 不然面板会因 comfyReady 恒为 false 而永远停在「模型加载中」。
  useEffect(() => {
    if (!enabled) return;
    if (startingFlow) return; // start() 自身已在轮询 /ready，避免并发重复
    if (state !== "running") return;
    if (comfyReady) return;

    let cancelled = false;
    const loop = async () => {
      while (!cancelled && mountedRef.current) {
        try {
          const res = await authedFetch("/ready");
          const body = (await res.json().catch(() => null)) as
            | { comfyReady?: boolean }
            | null;
          if (cancelled || !mountedRef.current) return;
          if (body?.comfyReady) {
            setComfyReady(true);
            return;
          }
        } catch {
          // 探测失败忽略，下一轮重试（不向面板抛错）。
        }
        await sleep(pollIntervalMs);
      }
    };
    void loop();
    return () => {
      cancelled = true;
    };
  }, [enabled, startingFlow, state, comfyReady, authedFetch, pollIntervalMs]);

  // 触发启动并轮询至就绪（Req 6.2, 6.3, 6.5）。
  const start = useCallback(() => {
    if (!enabled || startingFlow) return;

    const token = ++flowRef.current;
    const active = () => mountedRef.current && flowRef.current === token;
    const startedAt = Date.now();

    // 秒级计时器：驱动「已等待 / 预计还需」进度展示。
    setElapsedSec(0);
    const timer = window.setInterval(() => {
      if (!active()) {
        window.clearInterval(timer);
        return;
      }
      setElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    const run = async () => {
      setStartingFlow(true);
      setError(null);
      try {
        // 1) 幂等触发启动：POST /power/start（服务端复用 decideStart）。
        const res = await authedFetch("/power/start", { method: "POST" });
        if (!res.ok) {
          throw new Error(`启动实例失败（${res.status}）`);
        }
        const body = (await res.json()) as PowerStatusResponse;
        if (!active()) return;
        setState(body.state);
        setInstanceId(body.instanceId ?? instanceId);

        // 2) 轮询 status 至 running（覆盖 pending / stopped→running 过渡）。
        let cur: PowerState = body.state;
        while (active() && cur !== "running") {
          if (cur === "stopping" || cur === "unavailable") {
            // 非启动路径的过渡态：停止轮询，交由用户重试。
            throw new Error(`实例进入不可启动状态：${cur}`);
          }
          await sleep(pollIntervalMs);
          if (!active()) return;
          const s = await fetchStatus();
          if (!s) break;
          cur = s.state;
          setState(cur);
          setComfyReady(Boolean(s.comfyReady));
        }

        // 3) running 后探测 /ready 至 ComfyUI 就绪（覆盖 ~40s 冷加载）。
        while (active()) {
          const ready = await probeReady();
          if (!active()) return;
          if (ready) {
            setComfyReady(true);
            break;
          }
          setComfyReady(false);
          await sleep(pollIntervalMs);
        }
      } catch (err: unknown) {
        if (active()) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        window.clearInterval(timer);
        if (active()) setStartingFlow(false);
      }
    };

    // GET /ready → { comfyReady, ... }
    const probeReady = async (): Promise<boolean> => {
      const res = await authedFetch("/ready");
      // /ready 在上游不可用时以 503 等明确状态回应，不视为异常；据 body 判断。
      const body = (await res.json().catch(() => null)) as
        | { comfyReady?: boolean }
        | null;
      return Boolean(body?.comfyReady);
    };

    void run();
  }, [
    enabled,
    startingFlow,
    authedFetch,
    fetchStatus,
    pollIntervalMs,
    instanceId,
  ]);

  // 触发关机（管理员密码保护，Req：管理员专属关机）。
  const stop = useCallback(
    async (passcode: string): Promise<boolean> => {
      if (!enabled) return false;
      setError(null);
      try {
        const res = await authedFetch("/power/stop", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ passcode }),
        });
        if (res.status === 403) {
          if (mountedRef.current) setError("管理员密码错误");
          return false;
        }
        if (!res.ok) {
          throw new Error(`关机失败（${res.status}）`);
        }
        const body = (await res.json()) as PowerStatusResponse & {
          mustChangePasscode?: boolean;
        };
        if (!mountedRef.current) return true;
        // 取消任何进行中的启动流程并复位就绪。
        flowRef.current += 1;
        setStartingFlow(false);
        setComfyReady(false);
        setState(body.state);
        if (body.mustChangePasscode) setMustChangePasscode(true);
        return true;
      } catch (err: unknown) {
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : String(err));
        }
        return false;
      }
    },
    [enabled, authedFetch],
  );

  // 修改关机密码（管理员）：POST /power/passcode。
  const changePasscode = useCallback(
    async (
      currentPasscode: string,
      newPasscode: string,
    ): Promise<{ ok: boolean; message?: string }> => {
      if (!enabled) return { ok: false, message: "未登录" };
      try {
        const res = await authedFetch("/power/passcode", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ currentPasscode, newPasscode }),
        });
        if (res.ok) {
          if (mountedRef.current) setMustChangePasscode(false);
          return { ok: true };
        }
        const body = (await res.json().catch(() => null)) as
          | { message?: string }
          | null;
        return {
          ok: false,
          message: body?.message ?? `修改失败（${res.status}）`,
        };
      } catch (err: unknown) {
        return {
          ok: false,
          message: err instanceof Error ? err.message : String(err),
        };
      }
    },
    [enabled, authedFetch],
  );

  const clearMustChangePasscode = useCallback(() => {
    setMustChangePasscode(false);
  }, []);

  // enabled 关闭（登出）时复位启动流程。
  useEffect(() => {
    if (!enabled) {
      flowRef.current += 1;
      setStartingFlow(false);
    }
  }, [enabled]);

  // 面板的「可启动」判定以 shared 的 decideStart 为准（见 powerView.derivePowerView）。
  return {
    state,
    comfyReady,
    instanceId,
    loading,
    startingFlow,
    elapsedSec,
    error,
    refresh,
    start,
    stop,
    mustChangePasscode,
    clearMustChangePasscode,
    changePasscode,
  };
}
